import { updateStore } from 'core_main/store';
import { debug, handleError, LogLabel } from 'globals/errors';
import { resubscribeConversationChannelGroup } from './resubscribeConversationChannelGroup';

/** Bounded retries after PubNub "empty subscription set" (PROD-261 / first-convo race). */
const MAX_EMPTY_CHANNEL_GROUP_RETRIES = 3;
const EMPTY_CHANNEL_GROUP_BACKOFF_MS = [300, 800, 2000];

let emptyGroupRetryAttempt = 0;
let emptyGroupRetryTimeoutId: ReturnType<typeof setTimeout> | undefined;
let emptyGroupRetryIsCurrent: (() => boolean) | undefined;
let emptyGroupExhaustionReported = false;

export interface ScheduleEmptyChannelGroupResubscribeOptions {
  /** When false, a superseded PubNub client generation skips the retry. */
  isListenerCurrent?: () => boolean;
  customerId?: string;
  orgId?: string;
}

export interface ResetEmptyChannelGroupRecoveryOptions {
  /**
   * When true, keep the exhaustion latch so force re-init of the same outage
   * does not re-page Sentry (Baz). Cleared on Connected / new non-force init.
   */
  preserveExhaustionLatch?: boolean;
}

export const resetEmptyChannelGroupRecovery = (
  options: ResetEmptyChannelGroupRecoveryOptions = {},
): void => {
  emptyGroupRetryAttempt = 0;
  emptyGroupRetryIsCurrent = undefined;
  if (!options.preserveExhaustionLatch) {
    emptyGroupExhaustionReported = false;
  }
  if (emptyGroupRetryTimeoutId !== undefined) {
    clearTimeout(emptyGroupRetryTimeoutId);
    emptyGroupRetryTimeoutId = undefined;
  }
};

/**
 * Clears connected state and schedules a bounded channel-group resubscribe.
 * PubNub rejects subscribe while the group has zero channels; session create
 * usually fills the group within a few hundred ms.
 */
export const scheduleEmptyChannelGroupResubscribe = (
  options: ScheduleEmptyChannelGroupResubscribeOptions = {},
): void => {
  updateStore('isPubNubConnected', false);

  if (options.isListenerCurrent) {
    emptyGroupRetryIsCurrent = options.isListenerCurrent;
  }

  if (emptyGroupRetryAttempt >= MAX_EMPTY_CHANNEL_GROUP_RETRIES) {
    // PROD-2159 / PROD-1986: transient empty-group 400s are filtered from Sentry;
    // report only once when bounded recovery fails to reconnect.
    if (!emptyGroupExhaustionReported) {
      emptyGroupExhaustionReported = true;
      handleError(
        new Error(
          'Empty channel-group recovery exhausted: PubNub still disconnected',
        ),
        {
          message: `Empty channel-group recovery exhausted after ${MAX_EMPTY_CHANNEL_GROUP_RETRIES} attempts`,
          labels: [LogLabel.PUBNUB_RECOVERY],
          level: 'error',
          tags: {
            pubnubEmptyChannelGroupRetries: String(
              MAX_EMPTY_CHANNEL_GROUP_RETRIES,
            ),
          },
        },
      );
    }
    return;
  }

  const isCoalescingPendingRetry = emptyGroupRetryTimeoutId !== undefined;
  if (isCoalescingPendingRetry) {
    clearTimeout(emptyGroupRetryTimeoutId);
  } else {
    emptyGroupRetryAttempt += 1;
  }

  const delayMs =
    EMPTY_CHANNEL_GROUP_BACKOFF_MS[emptyGroupRetryAttempt - 1] ??
    EMPTY_CHANNEL_GROUP_BACKOFF_MS[EMPTY_CHANNEL_GROUP_BACKOFF_MS.length - 1];
  const attempt = emptyGroupRetryAttempt;

  debug({
    message: `Empty channel-group BadRequest: scheduling resubscribe attempt ${attempt}/${MAX_EMPTY_CHANNEL_GROUP_RETRIES} in ${delayMs}ms`,
    labels: [LogLabel.PUBNUB_RECOVERY],
  });

  emptyGroupRetryTimeoutId = setTimeout(() => {
    emptyGroupRetryTimeoutId = undefined;
    if (emptyGroupRetryIsCurrent && !emptyGroupRetryIsCurrent()) {
      debug({
        message: 'Empty channel-group retry skipped: stale client generation',
        labels: [LogLabel.PUBNUB_RECOVERY],
      });
      return;
    }
    resubscribeConversationChannelGroup({
      customerId: options.customerId,
      orgId: options.orgId,
    });
  }, delayMs);
};

export const markConversationChannelGroupConnected = (): void => {
  resetEmptyChannelGroupRecovery();
  updateStore('isPubNubConnected', true);
};
